/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getCookie, setCookieDaysTillExpired } from '@saviynt/common';
import {
  Box,
  Checkbox,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import moment from 'moment';

import { useInvertedLocalize } from '../../../utils/localization';
import { CHECKBOX_CONSTANTS } from '../../CertsActionButtons/constant';
import { TAB_STATUS_ALL } from '../../ViewControls/CertsControlTabs/constant';

import { invertedMessages } from './messages';

import './CertsAutoRevokeWarning.css';

const MIN_DAYS_FOR_REVOKE_WARNING = 7;
const REVOCABLE_CERTIFICATION_STATUSES = [0, 1, 2];

function CertsAutoRevokeWarning() {
  const [isVisible, setIsVisible] = useState(true);
  const [isEndDateInWarningRange, setIsEndDateInWarningRange] = useState(null);
  const [shouldNotShowWarningAgain, setShouldNotShowWarningAgain] =
    useState(false);
  const location = useLocation();
  const l = useInvertedLocalize(invertedMessages);

  const {
    global: { globalDateFormat },
    lineActions: { autoRevokeWarningList, certificationEndDate },
    reviewAndSubmit: { certificationStatus },
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const queryParams = new URLSearchParams(location?.search);
  const certKey = queryParams.get('certkey');
  const certificationEndDateTime = certificationEndDate
    ? moment(certificationEndDate).utc()
    : null;
  const revokeDueDate = certificationEndDateTime
    ? certificationEndDateTime.format(
        globalDateFormat?.toUpperCase() || 'DD/MM/YYYY'
      )
    : '';
  const autoRevokeCookieKey = `${CHECKBOX_CONSTANTS.AUTO_REVOKE_WARN}${certKey}`;
  const autoRevokeWarningFlag = getCookie(autoRevokeCookieKey);
  const shouldNotShowAutoRevokeWarning = autoRevokeWarningFlag === 'true';
  const allTabCount =
    tabData?.find((tab) => tab.key === TAB_STATUS_ALL)?.count || 0;
  const isCertificationRevocable =
    REVOCABLE_CERTIFICATION_STATUSES.includes(certificationStatus);
  const shouldShowAutoRevokeWarning =
    isVisible &&
    isEndDateInWarningRange &&
    autoRevokeWarningList.length > 0 &&
    isCertificationRevocable &&
    allTabCount > 0;

  useEffect(() => {
    // Check cookies for revoke warning flag
    if (shouldNotShowAutoRevokeWarning) setIsVisible(false);
  }, []);

  useEffect(() => {
    if (certificationEndDate) {
      const currentDate = moment().utc();
      const daysDifference = certificationEndDateTime.diff(currentDate, 'days');

      if (daysDifference < 0) {
        setIsEndDateInWarningRange(false);
      } else {
        setIsEndDateInWarningRange(
          daysDifference <= MIN_DAYS_FOR_REVOKE_WARNING
        );
      }
    }
  }, [certificationEndDate]);

  const setAutoRevokeCookie = (checkedState) => {
    const currentDate = moment().utc();
    const daysDifference = certificationEndDateTime.diff(currentDate, 'days');

    setCookieDaysTillExpired(
      autoRevokeCookieKey,
      checkedState,
      daysDifference + 1
    );
  };

  const handleCheckboxChange = (e) => {
    setShouldNotShowWarningAgain(e.target.checked);
    setAutoRevokeCookie(e.target.checked);
  };

  const headerDetail = l(
    `If you don’t submit your certification by the due date ({revokeDueDate}), some access will be automatically revoked.`,
    {
      revokeDueDate,
    }
  );

  return (
    <StackingAlertBanner
      colorTheme='Warning'
      kind='low'
      title={l('Automatic Access Revocation Notice')}
      description={headerDetail}
      isVisible={shouldShowAutoRevokeWarning}
      onCancel={() => setIsVisible(false)}
      expandButtonLabels={{ expanded: 'Show Less', collapsed: 'Show More' }}
      expandingContent={
        <Box>
          <Typography
            kind='body2'
            className='CertsAutoRevokeWarning-expandedBodyText'>
            {l(
              'This campaign has been configured so that the following items will be revoked after the due date:'
            )}
          </Typography>
          <Box className='CertsAutoRevokeWarning-expandedBodyList'>
            {autoRevokeWarningList?.map((value) => (
              <Typography
                key={value}
                kind='body2'
                className='CertsAutoRevokeWarning-expandedBodyText'>
                • {value}
              </Typography>
            ))}
          </Box>
        </Box>
      }
      content={
        <Checkbox
          id='auto-revoke-warning-checkbox'
          name='dontShowCheck'
          isChecked={shouldNotShowWarningAgain}
          onChange={handleCheckboxChange}
          label={l('Don’t show me this message again for this certification')}
          textPosition='after'
          showHoverEffect
        />
      }
    />
  );
}

export default CertsAutoRevokeWarning;
